import React from 'react';
import cx from 'classnames';

const getHelp = (help) => {
  if (!help) {
    return null;
  }

  return (
    <div className="hint">
      {help}
    </div>
  );
};

const getError = (error, hasError) => {
  if (!hasError || !error) {
    return null;
  }
  return (
    <div className="error">
      {error}
    </div>
  );
};

const isHorizontal = (locals) => {
  return (locals && locals.config && locals.config.horizontal);
};

const getStackedForm = (locals) => {
  return locals.order.map((inputLabel, index) => {
    return getStackedContainerComponent(locals.inputs[inputLabel], index);
  });
};

const getInlineForm = (locals) => {
  return locals.order.map((inputLabel, index) => {
    return getInlineContainerComponent(locals.inputs[inputLabel], index);
  });
};

const getInlineContainerComponent = (input, index) => {
  const _opts = {
    'form-group': true,
    'nr-form-cells-2': true
  };
  _opts['form-group-' + index] = true;
  const classes = cx(_opts);
  return (
    <div key={index} className={classes}>
      {input}
    </div>
  );
};

const getStackedContainerComponent = (input, index) => {
  const _opts = {
    'form-group': true,
    'nr-form-cells-1': true
  };
  _opts['form-group-' + index] = true;
  const classes = cx(_opts);
  return (
    <div key={index} className={classes}>
      {input}
    </div>
  );
};

function struct(locals) {
  if (isHorizontal(locals)) {
    return (
      <div className='form form-inline'>
        {getInlineForm(locals)}
      </div>
    );
  }
  return (
    <div className='form form-stacked'>
      {getStackedForm(locals)}
    </div>
  );
}

const getHiddenTextbox = (hiddenTextbox) => {
  return (<input type='hidden' value={hiddenTextbox.value} name={hiddenTextbox.name}/>);
};

const getStaticTextbox = (locals) => {
  return (
    <div>
      <div className="form-cell form-cell-0">
        <label>{locals.label}</label>
      </div>
      <div className="form-cell form-cell-1">
        {locals.value}
      </div>
    </div>
  );
};

function getInputClasses(hasError) {
  return cx({
    'form-cell': true,
    'form-cell-1': true,
    'has-error': hasError
  });
}

function getLabel(label, hasError) {
  if (!label) {
    return null;
  }
  const labelClasses = cx({
    'form-cell': true,
    'form-cell-0': true,
    'has-error': hasError
  });
  return (
    <div className={labelClasses}>
      <label>{label}</label>
    </div>
  );
}

function textbox(locals) {
  if (locals.type === 'hidden') {
    return getHiddenTextbox(locals);
  }

  if (locals.type === 'static') {
    return getStaticTextbox(locals);
  }

  return (
    <div>
      { getLabel(locals.label, locals.hasError) }
      <div className={getInputClasses(locals.hasError)}>
        <div className="input-wrapper">
          {getTextboxInput(locals)}
        </div>
      </div>
    </div>
  );
}

function getTextboxInput(locals) {
  const _onChange = (evt) => {
    return locals.onChange(evt.target.value);
  };

  const name = locals.attrs['name'];

  let tag = (<input id={locals.attrs[name]} type={locals.type} value={locals.value} onChange={_onChange} />);
  if (locals.type === 'textarea') {
    tag = (<textarea id={locals.attrs[name]} type={locals.type} value={locals.value} onChange={_onChange} />);
  }

  const classes = cx({
    'input-wrapper': locals.isHorizontal
  });
  if (locals.type !== 'textarea') {
    return (
      <div className={classes}>
        {tag}
        { getError(locals.error, locals.hasError) }
        { getHelp(locals.help) }
      </div>
    );
  }
}

function getCheckboxInput(locals) {
  const _onChange = (evt) => {
    return locals.onChange(evt.target.checked);
  };

  const classes = cx({
    'input-wrapper': locals.isHorizontal ? true : false
  });

  return (
    <div className={classes}>
      <input id={locals.attrs.id} type='checkbox' disbled={locals.disabled} value={locals.value} onChange={_onChange} />
    </div>
  );
}

function checkbox(locals) {
  const inputClasses = cx({
    'form-cell': true,
    'form-cell-0': true,
    'has-error': locals.hasError
  });

  return (
    <div>
      <div className={inputClasses}>
        {getCheckboxInput(locals)}
      </div>
      <div className={getInputClasses(locals.hasError)}>
        <label>{locals.label}</label>
        { getError(locals.error, locals.hasError) }
        { getHelp(locals.help) }
      </div>
    </div>
  );
}

function select(locals) {
  return (
    <div>
      { getLabel(locals.label, locals.hasError) }
      <div className={getInputClasses(locals.hasError)}>
        <div className="input-wrapper">
          {getSelectInput(locals)}
        </div>
      </div>
    </div>
  );
}

function getSelectInput(locals) {
  const _onChange = (evt) => {
    let value = locals.isMultiple ? Array.prototype.slice.call(evt.target.options).filter(function (option) {
      return option.selected;
    }).map(function (option) {
      return option.value;
    }) : evt.target.value;
    locals.onChange(value);
  };

  const classes = cx({
    'input-wrapper': locals.isHorizontal ? true : false
  });

  const getOptions = () => {
    return locals.options.map((opt, index) => {
      return (<option key={index} value={opt.value}>{opt.text}</option>);
    });
  };

  return (
    <div className={classes}>
      <select id={locals.attrs.id} multiple={locals.multiple} disbled={locals.disabled} value={locals.value} onChange={_onChange}>
        {getOptions()}
      </select>
      { getError(locals.error, locals.hasError) }
      { getHelp(locals.help) }
    </div>
  );
}

function radio(locals) {
  return (
    <div>
      { getLabel(locals.label, locals.hasError) }
      <div className={getInputClasses(locals.hasError)}>
        <div className="input-wrapper">
          {getRadioInput(locals)}
        </div>
      </div>
    </div>
  );
}

function getRadioInput(locals) {
  const _onChange = (evt) => {
    return locals.onChange(evt.target.value);
  };

  let id = locals.attrs.id;
  return locals.options.map((opt, index) => {
    const isChecked = (opt.value === locals.value);
    const optId = id + '_' + index;

    return (
      <div key={index} id={locals.attrs.name}>
        <div className='group-input'>
          <input id={optId} type='radio' disabled={locals.disabled} checked={isChecked} value={opt.value} onChange={_onChange} />
        </div>
        <div className='group-label'>
          {opt.text}
        </div>
      </div>
    );
  });


}


function range(n) {
  var result = [];
  for (var i = 1; i <= n; i++) {
    result.push(i);
  }
  return result;
}

function padLeft(x, len) {
  var str = String(x);
  var times = len - str.length;
  for (var i = 0; i < times; i++) {
    str = '0' + str;
  }
  return str;
}

function toOption(value, text, key) {
  return (<option key={key} value={value}>{text}</option>);
}

const nullOption = (key) => {
  return [toOption('', '-', key + '-0')];
};

const optionsDays = nullOption('days').concat(range(31).map(function (i) {
  return toOption(i, padLeft(i, 2), 'days-' + i);
}));

const optionsMonths = nullOption('months').concat(range(12).map(function (i) {
  return toOption(i - 1, padLeft(i, 2), 'months-' + i);
}));


function date(locals) {
  return (
    <div>
      { getLabel(locals.label, locals.hasError) }
      <div className={getInputClasses(locals.hasError)}>
        <div className="input-wrapper">
          {getDateInput(locals)}
        </div>
      </div>
    </div>
  );
}

function getDateInput(locals) {
  let value = locals.value.slice();

  const onDayChange = (evt) => {
    value[2] = evt.target.value === '-' ? null : evt.target.value;
    locals.onChange(value);
  };

  const onMonthChange = (evt) => {
    value[1] = evt.target.value === '-' ? null : evt.target.value;
    locals.onChange(value);
  };

  const onYearChange = (evt) => {
    value[0] = evt.target.value.trim() === '' ? null : evt.target.value.trim();
    locals.onChange(value);
  };

  const days = () => {
    return (
      <select key='days' className='days' disabled={locals.disabled} value={value[2]} onChange={onDayChange}>
        {optionsDays}
      </select>
    );
  };

  const months = () => {
    return (
      <select key='months' className='months' disabled={locals.disabled} value={value[1]} onChange={onMonthChange}>
        {optionsMonths}
      </select>
    );
  };

  const years = () => {
    return (
      <input key='years' className='years' type="text" disabled={locals.disabled} maxLength={4} size={4}  value={value[0]} onChange={onYearChange} />
    );
  };

  const parts = {
    D: days(),
    M: months(),
    YY: years()
  };

  const children = locals.order.map(function (id) {
    return parts[id];
  });

  const classes = cx({
    'input-wrapper': locals.isHorizontal ? true : false
  });
  return (
    <div className={classes}>
      {children}
      { getError(locals.error, locals.hasError) }
      { getHelp(locals.help) }
    </div>
  );
}

function list() {
  return (<div>list</div>);
}

export default  {
  textbox,
  checkbox,
  select,
  radio,
  date,
  struct,
  list
};



/** WEBPACK FOOTER **
 ** C:/repository/madai/v6/client/lib/web/jsdkTemplate.js
 **/